import { Fragment, type ReactNode } from 'react';

/**
 * Kleiner Markdown-Renderer für die Rechtstexte in legal/*.md: Überschriften, Absätze (einzelne
 * Zeilenumbrüche bleiben erhalten – wichtig für Anschriften), Listen, **fett**, [Links](…).
 * Erzeugt React-Elemente, kein innerHTML; HTML-Kommentare (Bearbeitungshinweise) entfallen.
 */
export function renderMarkdown(source: string): ReactNode[] {
  const text = source.replace(/<!--[\s\S]*?-->/g, '').replace(/\r\n/g, '\n');
  const blocks = text.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  return blocks.map((block, i) => {
    const heading = /^(#{1,3})\s+(.*)$/.exec(block);
    if (heading && !block.includes('\n')) {
      const level = heading[1]!.length;
      const content = inline(heading[2]!);
      return level === 1 ? <h1 key={i} className="page__title">{content}</h1> : level === 2 ? <h2 key={i}>{content}</h2> : <h3 key={i}>{content}</h3>;
    }
    const lines = block.split('\n');
    if (lines.every((l) => /^[-*]\s+/.test(l))) {
      return (
        <ul key={i}>
          {lines.map((l, j) => (
            <li key={j}>{inline(l.replace(/^[-*]\s+/, ''))}</li>
          ))}
        </ul>
      );
    }
    return (
      <p key={i}>
        {lines.map((l, j) => (
          <Fragment key={j}>
            {j > 0 && <br />}
            {inline(l)}
          </Fragment>
        ))}
      </p>
    );
  });
}

function safeHref(href: string): string | undefined {
  return /^(https:\/\/|mailto:|\/|#|\.\.?\/)/.test(href) ? href : undefined;
}

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1] !== undefined) out.push(<strong key={m.index}>{inline(m[1])}</strong>);
    else {
      const href = safeHref(m[3]!);
      out.push(href ? <a key={m.index} href={href}>{m[2]}</a> : m[2]);
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
